import React from 'react';
import styled from 'styled-components/macro';
import { useLocalStore, observer } from 'mobx-react-lite';
import {
  ProductHeroDetailProps,
  ProductHeroProps,
} from '../types/ProductHeroProps';
import ProductLabels from './ProductLabels';
import ProductActionButtons from './ProductActionButtons';
// import { ProductAddNote } from './ProductAddNote';
import { ProductAward } from './ProductAward';
import PriceDisplay from './PriceDisplay';
import ProductVariationSelect from './ProductVariationSelect';
import { TypeProduct } from '../types/TypeProduct';
import { parseBoolean } from '../utils/parseBoolean';
import { useFallbackImage } from '../utils/useFallbackImage';
import { useProductViewEvent } from '../utils/hooks/useProductViewEvent';

const UnstyledDetailsSection: React.FC<ProductHeroDetailProps> = observer(
  (props) => {
    // const noteProps = {
    //   note: props.note,
    //   onNoteInput: props.onNoteInput
    // }
    return (
      <div className={'ww-product-subdetails ' + (props?.className ?? '')}>
        {props.data && (
          <ProductActionButtons
            {...{
              tried: props.data.tried,
              liked: props.data.liked,
              onLiked: props.data.isWine ? props.data.onLiked : undefined,
              onTried: props.data.isWine ? props.data.onTried : undefined,
              onBuy: props.selectedVariation.isSaleableWithStock
                ? props.selectedVariation.onBuy
                : undefined,
              likebutton: props.likebutton,
              buybutton: props.buybutton,
              trybutton: props.trybutton,
            }}
          />
        )}
        {/*props.data.isWine && props.data.tried && <ProductAddNote {...noteProps} key={props.subkey} />*/}
        {props.data?.details && props.data.details.trim() && (
          <>
            <h3 className="wine-detail-header">Description</h3>
            <p dangerouslySetInnerHTML={{ __html: props.data.details }}></p>
          </>
        )}
        {props.data?.productAwards && (
          <div className="section">
            {props.data.productAwards &&
              props.data.productAwards.map((a) => (
                <ProductAward data={a} key={'pa_' + a.id}></ProductAward>
              ))}
          </div>
        )}
        {props.stats && props.stats.length > 0 && (
          <h3 className="wine-detail-header">{props.detailsHeader}</h3>
        )}
        {props.stats && props.stats.length > 0 && (
          <ul className="subdetails">
            {props.stats.map(({ spec, value, autocol, isHtml }) => {
              const cls = `subdetail${autocol ? ' autocol' : ''}`;
              return (
                <li key={'spec-' + spec} className={cls}>
                  <span className="spec">{spec}</span>{' '}
                  {isHtml && value && typeof value === 'string' ? (
                    <span dangerouslySetInnerHTML={{ __html: value }}></span>
                  ) : (
                    <span>{value}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {props.data?.giftCardTermsAndConditionLink && (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={props.data.giftCardTermsAndConditionLink}
          >
            View Gift Card Terms &amp; Conditions
          </a>
        )}
      </div>
    );
  },
);

export const DetailsSection = styled(UnstyledDetailsSection)`
  h3.wine-detail-header {
    font-size: 1.17em;
    font-weight: bold;
    text-align: left;
    margin: 3em 0 1em 0;
  }

  .subdetails {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .subdetail {
    margin: 0 0 1em 0;
    display: flex;
    flex-direction: row;
  }

  .subdetail span {
    flex: 3 3;
  }

  .subdetail span.spec {
    flex: 2 2;
    font-weight: 600;
    margin-right: 0.5em;
  }

  .subdetail span p:first-child {
    margin-top: 0;
  }

  @media (max-width: 852px) {
    .subdetail.autocol {
      flex-direction: column;
    }

    .subdetail.autocol span {
      flex: none;
    }

    .subdetail.autocol span.spec {
      margin-bottom: 0.5em;
    }
  }
`;

const UnstyledProductHero: React.FC<ProductHeroProps> = observer((props) => {
  const { ...detailsProps } = { ...props };

  const state = useLocalStore(() => ({
    get selectedVariation() {
      return !parseBoolean(props.enablevariations, true)
        ? props.data
        : ((props.data?.selectedVariation ?? props.data) as TypeProduct);
    },
    get isSaleable() {
      return state.selectedVariation.isSaleableWithStock;
    },
    get imageHero() {
      return state.selectedVariation.imageHero ?? props.data.imageHero;
    },
    get showDisplayQty() {
      if (state.selectedVariation.websiteProductStock) {
        return (
          state.isSaleable &&
          state.selectedVariation.websiteProductStock.count <=
            state.selectedVariation.websiteProductStock.displayQuantityLeftAt &&
          state.selectedVariation.websiteProductStock.count > 0 &&
          !!state.selectedVariation.websiteProductStock.quantityLeft
        );
      }
      return state.isSaleable;
    },
  }));

  const fallbackImage = useFallbackImage(state.imageHero);
  useProductViewEvent(state.selectedVariation);

  return (
    <div className={'ww-product-hero ' + (props.className ?? '')}>
      <div className="head-section">
        <div className="image-container">
          <img
            className="image"
            onError={fallbackImage}
            src={state.imageHero}
            alt={props.data.name}
          />
        </div>
        <div className="content">
          <h1 className="name">
            {props.data.brandNameOverride && (
              <small className="name-override">
                {props.data.brandNameOverride}
              </small>
            )}
            {props.data.name}
          </h1>
          {props.data.kind && <p className="kind">{props.data.kind}</p>}
          {((props.data.region && props.data.region !== 'Multiple') ||
            (props.data.state && props.data.state !== 'Multiple')) && (
            <p className="region-state">
              {[props.data.subRegion, props.data.region, props.data.state]
                .filter((item) => !!item && item !== 'Multiple')
                .join(', ')}
            </p>
          )}
          {props.data.desc && <p className="description">{props.data.desc}</p>}

          {parseBoolean(props.enablevariations, true) &&
            props.data.variations &&
            props.data.variations.length > 0 && (
              <p className="ww-product-variation">
                <ProductVariationSelect
                  data={props.data}
                  variationsort={props.variationsort}
                />
              </p>
            )}

          <PriceDisplay
            data={state.selectedVariation}
            priceFormat={props.priceFormat}
          />

          {props.labels && (
            <ProductLabels labels={props.labels} asWarning={true} />
          )}

          {state.selectedVariation.isSaleable &&
            state.selectedVariation.websiteProductStock
              ?.displayOutOfStockMessage && (
              <div className="out-of-stock-info">
                {state.selectedVariation.websiteProductStock
                  ?.outOfStockMessage ?? 'Out of stock'}
              </div>
            )}

          {state.selectedVariation.isSaleable && state.showDisplayQty && (
            <div className="quantity-info">
              {state.selectedVariation.websiteProductStock?.quantityLeft}
            </div>
          )}

          {props.data.isSaleable &&
            props.carrot &&
            (props.carrot.text || props.carrot.action) && (
              <div className="price-carrot">
                {props.carrot.text && (
                  <span className="price-carrot-text">{props.carrot.text}</span>
                )}
                {props.carrot.action && (
                  <a
                    className="price-carrot-action"
                    href={props.carrot.url}
                    target={props.carrot.target}
                  >
                    {props.carrot.action}
                  </a>
                )}
              </div>
            )}

          <div className="details">
            <DetailsSection
              subkey={'ph-d-s'}
              {...detailsProps}
              stats={props.data.stats}
              detailsHeader="Wine Details"
              selectedVariation={state.selectedVariation}
            />
          </div>
        </div>
      </div>
      <div className="details-mobile">
        <DetailsSection
          subkey={'ph-d-m'}
          {...detailsProps}
          stats={props.data.stats}
          detailsHeader="Wine Details"
          selectedVariation={state.selectedVariation}
        />
      </div>
    </div>
  );
});

export const ProductHero = styled(UnstyledProductHero)`
  display: flex;
  flex-direction: column;
  color: var(--gray-5);

  .head-section {
    display: flex;
    flex-direction: row;
    min-height: 50vh;
    padding: 0.5em;
  }

  .section {
    margin: 40px 0;
  }

  .image-container {
    flex: 1 1;
    padding: 0.5em;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
    align-self: center;
  }

  .image-container img.image {
    object-fit: contain;
    height: 100%;
    object-position: top;
  }

  .content {
    flex: 2 2;
    padding: 0.5em;
    position: relative;
  }

  .out-of-stock-info {
    color: var(--white);
    background-color: var(--warn-color);
    font-size: 0.785em;
    font-weight: 600;
    padding: 0.196em 1.176em;
    display: inline-block;
  }

  .quantity-info {
    color: var(--white);
    background-color: var(--info-color);
    font-size: 0.785em;
    font-weight: 600;
    padding: 0.196em 1.176em;
    display: inline-block;
  }

  h1.name .name-override {
    display: block;
    font-weight: 400;
    font-size: 0.7em;
    color: var(--gray-6, #888888);
  }

  h1.name {
    font-size: 1.9em;
    font-weight: 200;
  }

  .kind,
  .region-state {
    margin: 0;
  }

  .price-info-content {
    font-weight: bold;
    color: var(--primary-color);
  }

  .details {
    display: none;
  }

  .details-mobile {
    padding: 0.5em 1em;
    display: flex;
    flex: auto;
    flex-direction: column;
  }

  .ww-product-add-notes {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    color: var(--gray-6, #888888);
    margin-top: 1em;
  }

  .ww-product-add-notes i {
    flex: none;
    padding-top: 0.35em;
  }

  .ww-product-add-notes textarea {
    padding: 0.25em;
    appearance: none;
    border: none;
    outline: none;
    font-family: 'Open Sans', helvetica, arial, sans-serif;
  }

  .price-carrot {
    font-weight: bold;
  }

  .price-carrot-text {
    margin-right: 5px;
  }

  .variation-select {
    width: 100%;
    height: 2.5em;
    text-align: left;
    font-size: 1.077em;
  }

  @media (min-width: 569px) {
    width: 100%;
    max-width: 1024px;
    padding: 1em 2em;

    h1.name {
      font-size: 2.5em;
      font-weight: 200;
    }

    .image-container {
      height: 80vh;
      max-height: 500px;
      align-self: flex-start;
    }

    .details-mobile {
      display: none;
    }

    .details {
      display: flex;
      flex-direction: column;
    }
  }
`;
